import React from 'react';
import PropTypes from 'prop-types';
import AttachmentPreview from 'src/components/AttachmentPreview';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
} from '@mui/material';
import { getTemplateContextScopeConfig } from 'src/constants/templateContextScope';

/**
 * CON-1625 — verify-then-apply form for a Message Template share code.
 *
 * Mirrors SmartLinksView/ApplyShareCode/ApplyShareCodeForm: the user pastes
 * a code, Verify shows a read-only preview, and only then does the modal's
 * Apply become meaningful. The preview shows the scope chip because a Group
 * or Event template can only be used where those Smart Fields resolve, and
 * that is worth knowing BEFORE importing it.
 */
function ApplyShareCodeForm({
  shareCode = '',
  verified = null,
  verifyError = '',
  loading = false,
  onChangeShareCode = () => {},
  onVerify = () => {},
}) {
  const template = verified?.messageTemplate || null;
  const scopeConfig = template ? getTemplateContextScopeConfig(template.contextScope) : null;

  return (
    <Box sx={{ pt: 1 }}>
      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
        Paste a Message Template share code below, then click Verify to see
        what it is before you add it.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          fullWidth
          label="Share Code"
          value={shareCode}
          onChange={(e) => onChangeShareCode(e.target.value.trim())}
          error={!!verifyError}
          helperText={verifyError || ' '}
          disabled={loading}
          sx={{ '& input': { fontFamily: 'monospace' } }}
        />
        <Button
          onClick={onVerify}
          color="primary"
          disabled={loading || !shareCode}
          sx={{ mt: 1, whiteSpace: 'nowrap' }}
        >
          {loading ? <CircularProgress size={16} /> : 'Verify'}
        </Button>
      </Box>

      {template && (
        <Box sx={{
          mt: 2,
          p: 2,
          border: '1px solid #C7C7C7',
          borderRadius: '4px',
          backgroundColor: '#FAFAFA',
        }}
        >
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            mb: 1,
            flexWrap: 'wrap',
          }}
          >
            <Typography variant="subtitle2">
              {template.description || 'Message Template'}
            </Typography>
            {scopeConfig && (
              <Box
                component="span"
                sx={{
                  display: 'inline-block',
                  px: 1,
                  borderRadius: '10px',
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  lineHeight: '20px',
                  whiteSpace: 'nowrap',
                  backgroundColor: scopeConfig.bg,
                  color: scopeConfig.color,
                }}
              >
                {scopeConfig.label}
              </Box>
            )}
          </Box>

          {/* The template itself: text on the left, thumbnail on the right.
              Kept in ONE bordered block so the message is visually a single
              thing, separate from who it came from and what will happen. */}
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
            <Typography
              variant="body2"
              sx={{
                whiteSpace: 'pre-wrap', flexGrow: 1, flexShrink: 1, minWidth: 0
              }}
            >
              {template.systemGenMessage || '(no text)'}
            </Typography>

            <AttachmentPreview
              attachment={template.attachment}
              attachmentContainer={template.attachmentContainer}
              ownerSalespersonId={template.attachmentOwnerSalespersonId}
            />
          </Box>

          {/* A non-image attachment still has to be disclosed; it just has no
              thumbnail to show. */}
        </Box>
      )}

      {/* SECOND BLOCK: where it came from and what applying does. Split out
          of the preview above because running provenance, the own-code
          notice, and the numbering note together as three grey captions made
          them hard to absorb. */}
      {template && (
        <Box sx={{ mt: 2 }}>
          {verified?.isOwnCode ? (
            // Matches the Share Center wording for the same situation rather
            // than inventing a second phrasing for one idea.
            <Alert severity="info">
              This is your own Message Template share code. Applying it will
              create a second copy in your account, with your next available
              template number.
            </Alert>
          ) : (
            <>
              {verified?.ownerName && (
                <Typography variant="body2">
                  {`Shared by ${verified.ownerName}`}
                </Typography>
              )}
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 0.5 }}>
                It will be added to your templates with your own next available
                template number.
              </Typography>
            </>
          )}
        </Box>
      )}
    </Box>
  );
}

ApplyShareCodeForm.propTypes = {
  shareCode: PropTypes.string,
  verified: PropTypes.object,
  verifyError: PropTypes.string,
  loading: PropTypes.bool,
  onChangeShareCode: PropTypes.func,
  onVerify: PropTypes.func,
};

export default ApplyShareCodeForm;
